import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { FileText, Download, Search, IndianRupee, Calendar, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GST_RATE = 0.18;

const InvoicesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const res = await axios.get(`${API}/invoices`);
      setInvoices(res.data);
    } catch {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (contractId) => {
    setDownloading(contractId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/invoices/${contractId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-TP-${contractId.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded!');
    } catch {
      toast.error('Failed to download invoice');
    } finally {
      setDownloading(null);
    }
  };

  const filtered = invoices.filter(inv => {
    const term = search.toLowerCase();
    return (
      inv.freelancer_name?.toLowerCase().includes(term) ||
      inv.client_name?.toLowerCase().includes(term) ||
      inv.id?.toLowerCase().includes(term)
    );
  });

  const formatDate = (dt) => {
    if (!dt) return '—';
    try { return new Date(dt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return '—'; }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-96">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-2" data-testid="invoices-title">
            GST Invoices
          </h1>
          <p className="text-muted-foreground">Download PDF invoices for all your paid contracts</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card data-testid="total-invoices-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Invoices</p>
                  <p className="text-2xl font-heading font-bold">{invoices.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="total-value-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <IndianRupee className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Value</p>
                  <p className="text-2xl font-heading font-bold text-green-600">
                    ₹{invoices.reduce((s, i) => s + (i.amount || 0), 0).toLocaleString('en-IN')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="total-gst-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total GST (18%)</p>
                  <p className="text-2xl font-heading font-bold text-amber-600">
                    ₹{(invoices.reduce((s, i) => s + (i.amount || 0), 0) * GST_RATE).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-10"
            placeholder="Search by name or contract ID..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="invoice-search"
          />
        </div>

        {/* Invoice List */}
        {filtered.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground" data-testid="no-invoices-message">
                {invoices.length === 0
                  ? 'No invoices yet. Invoices are generated after payment is made on a contract.'
                  : 'No invoices match your search.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map((inv) => {
              const gst = inv.amount * GST_RATE;
              const total = inv.amount + gst;
              const isClient = inv.client_id === user?.id;
              return (
                <Card key={inv.id} className="hover:shadow-md transition-shadow" data-testid={`invoice-${inv.id}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FileText className="w-5 h-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-heading font-semibold text-sm">
                              INV-TP-{inv.id.slice(0, 8).toUpperCase()}
                            </span>
                            <Badge variant={inv.payment_status === 'released' ? 'default' : 'secondary'} className="text-xs">
                              {inv.payment_status}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {isClient ? `To: ${inv.freelancer_name}` : `From: ${inv.client_name}`}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(inv.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm text-muted-foreground">Base: ₹{inv.amount.toLocaleString('en-IN')}</div>
                        <div className="text-xs text-muted-foreground">GST: ₹{gst.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
                        <div className="font-heading font-bold text-secondary">
                          ₹{total.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/contracts/${inv.id}`)}
                          data-testid={`view-contract-${inv.id}`}
                        >
                          View
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleDownload(inv.id)}
                          disabled={downloading === inv.id}
                          data-testid={`download-invoice-${inv.id}`}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          {downloading === inv.id ? 'Downloading...' : 'PDF'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <p className="text-xs text-muted-foreground text-center mt-8">
          All invoices include 18% GST as per Indian tax regulations. GSTIN must be configured by admin.
        </p>
      </div>
    </div>
  );
};

export default InvoicesPage;
